import { useEffect, useState, useMemo } from "react";
import { OfficerLayout } from "@/components/OfficerLayout";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import { formatINR } from "@/lib/i18n";
import { Map as MapIcon, Filter } from "lucide-react";

// Fix default icon paths for Leaflet in webpack
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const TYPE_COLOR = {
  residential: "#0A3161",
  commercial: "#E87722",
  industrial: "#107C10",
  mixed: "#7C3AED",
};

function coloredIcon(color) {
  const html = `<div style="
    background:${color};
    width:22px;height:22px;
    border-radius:50% 50% 50% 0;
    transform:rotate(-45deg);
    border:2px solid white;
    box-shadow:0 2px 5px rgba(0,0,0,0.3);
  "></div>`;
  return L.divIcon({ html, className: "", iconSize: [22, 22], iconAnchor: [11, 22] });
}

export default function PropertiesMap() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const [properties, setProperties] = useState([]);
  const [typeFilter, setTypeFilter] = useState("");

  useEffect(() => {
    const params = {};
    if (user?.panchayat_id) params.panchayat_id = user.panchayat_id;
    api.get("/properties", { params }).then((r) => setProperties(r.data));
  }, [user]);

  const filtered = useMemo(() => {
    const withCoords = properties.filter((p) => p.gis_lat && p.gis_lng);
    if (!typeFilter) return withCoords;
    return withCoords.filter((p) => p.property_type === typeFilter);
  }, [properties, typeFilter]);

  const center = filtered[0]
    ? [filtered[0].gis_lat, filtered[0].gis_lng]
    : [28.89, 76.60]; // default to Rohtak area

  const counts = useMemo(() => {
    const c = { residential: 0, commercial: 0, industrial: 0, mixed: 0 };
    filtered.forEach((p) => { c[p.property_type] = (c[p.property_type] || 0) + 1; });
    return c;
  }, [filtered]);

  return (
    <OfficerLayout>
      <div>
        <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-[#E87722]">GIS</div>
            <h1 className="mt-2 font-heading text-3xl font-bold text-slate-900 flex items-center gap-2">
              <MapIcon className="w-7 h-7 text-[#0A3161]" /> {lang === "hi" ? "गाँव डिजिटल मानचित्र" : "Village Digital Twin"}
            </h1>
            <p className="mt-1 text-sm text-slate-600">
              {lang === "hi" ? "हर भू-संपत्ति का स्थान — श्रेणी के अनुसार रंगांकित।" : "Every geo-tagged property color-coded by category."}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} data-testid="map-type-filter"
              className="px-3 py-2 rounded-md border border-slate-300 bg-white text-sm">
              <option value="">All types</option>
              <option value="residential">{t("residential")}</option>
              <option value="commercial">{t("commercial")}</option>
              <option value="industrial">{t("industrial")}</option>
              <option value="mixed">{t("mixed")}</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {Object.entries(counts).map(([k, v]) => (
            <div key={k} className="rounded-md border border-slate-200 bg-white p-4 flex items-center gap-3">
              <span className="w-3 h-3 rounded-full" style={{ background: TYPE_COLOR[k] }} />
              <div>
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{t(k)}</div>
                <div className="font-heading text-xl font-bold text-slate-900">{v}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 rounded-md border border-slate-200 bg-white overflow-hidden" style={{ height: 560 }} data-testid="gis-map">
          <MapContainer center={center} zoom={14} style={{ height: "100%", width: "100%" }} scrollWheelZoom={true}>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {filtered.map((p) => (
              <Marker key={p.id} position={[p.gis_lat, p.gis_lng]} icon={coloredIcon(TYPE_COLOR[p.property_type] || "#0A3161")}>
                <Popup>
                  <div className="text-xs">
                    <div className="font-bold text-sm text-slate-900">{p.owner_name}</div>
                    <div className="font-mono text-slate-500 mb-1">{p.assessment_no}</div>
                    <div className="capitalize">{t(p.property_type)} · {p.ward}</div>
                    <div className="mt-1">{p.address}</div>
                    <div className="mt-2 pt-1 border-t border-slate-100">
                      <span className="text-slate-500">ARV: </span>
                      <span className="font-mono font-semibold">{formatINR(p.annual_rental_value)}</span>
                    </div>
                  </div>
                </Popup>
              </Marker>
            ))}
          </MapContainer>
        </div>

        {filtered.length === 0 && (
          <div className="mt-3 text-sm text-slate-500">
            No geo-tagged properties yet. Add latitude/longitude when creating an assessment.
          </div>
        )}
      </div>
    </OfficerLayout>
  );
}
